import * as Vue from 'vue';
import { inject, computed, onMounted, onUpdated, reactive, ref } from 'vue';
import { ContextHolder, FeatureFlags, USE_ENTITLEMENTS_V2_ENDPOINT_FF, FronteggFrameworks } from '@frontegg/rest-api';
export { ContextHolder } from '@frontegg/rest-api';
import set from 'set-value';
import { isAuthRoute, authInitialState, loginActions, socialLoginsActions, activateAccountActions, acceptInvitationActions, forgotPasswordActions, signUpActions, profileActions, ssoActions, mfaActions, teamActions, apiTokensActions, securityPolicyActions, tenantsActions, bindActionCreators, authActions, isSteppedUp, defaultFronteggRoutes, FRONTEGG_AFTER_AUTH_REDIRECT_URL, getFeatureEntitlements, getPermissionEntitlements, getEntitlements } from '@frontegg/redux-store';
import get from 'get-value';
import { initialize, AdminPortal } from '@frontegg/js';
export { AdminPortal } from '@frontegg/js';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const FRONTEGG_STORE_KEY = 'FRONTEGG_STORE_KEY';
const FRONTEGG_REDUX_BINDINGS = 'FRONTEGG_REDUX_BINDINGS';
const FRONTEGG_UNSUBSCRIBE = 'FRONTEGG_UNSUBSCRIBE';
const FRONTEGG_LOADER_UNSUBSCRIBE = 'FRONTEGG_LOADER_UNSUBSCRIBE';
const fronteggLoadedKey = Symbol('fronteggLoade');
const authStateKey = Symbol('authState');
const unsubscribeFronteggStoreKey = Symbol('unsubscribeFronteggStore');
const fronteggAuthKey = Symbol('fronteggAuth');
const loadEntitlementsKey = Symbol('loadEntitlements');
const routerKey = Symbol('router');
const fronteggOptionsKey = Symbol('fronteggOptions');
const fronteggStoreKey = Symbol('fronteggStore');
const stepUpKey = Symbol('stepUp');

const setStoreKey = (instance, store) => {
  var _a;

  return instance[FRONTEGG_STORE_KEY] = (_a = instance[FRONTEGG_STORE_KEY]) !== null && _a !== void 0 ? _a : store;
};
const getStore = instance => instance[FRONTEGG_STORE_KEY];
const getStoreBinding = instance => instance[FRONTEGG_REDUX_BINDINGS];
const setStoreBinding = (instance, data) => instance[FRONTEGG_REDUX_BINDINGS] = data;
const setStoreUnsubscribe = (instance, unsubscribe) => instance[FRONTEGG_UNSUBSCRIBE] = unsubscribe;
const storeUnsubscribe = instance => {
  var _a;

  return (_a = instance[FRONTEGG_UNSUBSCRIBE]) === null || _a === void 0 ? void 0 : _a.call(instance);
}; // export const setMapState = (instance: any) => {instance.mapState = (...props: any[]) => mapState(instance, ...props);};

const setLoadingUnsubscribe = (instance, unsubscribe) => instance[FRONTEGG_LOADER_UNSUBSCRIBE] = unsubscribe;
const loadingUnsubscribe = instance => {
  var _a;

  return (_a = instance[FRONTEGG_LOADER_UNSUBSCRIBE]) === null || _a === void 0 ? void 0 : _a.call(instance);
};
const registerFronteggPlugin = (Vue, plugin) => {
  var _a;

  Vue.fronteggPlugins = (_a = Vue.fronteggPlugins) !== null && _a !== void 0 ? _a : [];
  Vue.fronteggPlugins.push(plugin);
};

class StoreHolder {
  static getInstance() {
    if (!StoreHolder.instance) {
      StoreHolder.instance = new StoreHolder();
    }

    return StoreHolder.instance;
  }

  static setStore(store) {
    StoreHolder.getInstance().store = store;
  }

  static getStore() {
    return StoreHolder.getInstance().store;
  }

  static setBasename(baseName) {
    StoreHolder.getInstance().baseName = baseName;
  }

  static getBasename() {
    return StoreHolder.getInstance().baseName;
  }

}

const setupOnRedirectTo = (router, routes) => {
  const baseName = getRouterBaseName(router) || '';
  StoreHolder.setBasename(baseName);

  const onRedirectTo = (_path, opts) => {
    let path = _path;

    if (path.startsWith(baseName) && baseName !== '/') {
      path = path.substring(baseName.length - 1);
    }

    if ((opts === null || opts === void 0 ? void 0 : opts.preserveQueryParams) || isAuthRoute(path, routes)) {
      path = `${path}${window.location.search}`;
    }

    if (opts === null || opts === void 0 ? void 0 : opts.refresh) {
      window.location.href = path;
    } else {
      if (opts === null || opts === void 0 ? void 0 : opts.replace) {
        router.replace(path);
      } else {
        router.push(path);
      }
    }
  };

  ContextHolder.setOnRedirectTo(onRedirectTo);
  return onRedirectTo;
};
const syncStateWithComponent = (component, bindings) => () => {
  const state = getStore(component).getState();
  Object.keys(bindings).forEach(subState => {
    Object.keys(bindings[subState]).forEach(prop => {
      const getter = bindings[subState][prop];

      if (!getter) {
        return;
      }

      const newData = getter(get(state, subState));
      const vueObject = Object.assign({}, Vue);
      let version;
      if (vueObject.default) version = 2;

      if (vueObject.version && vueObject.version[0] === '3') {
        version = 3;
      }

      if (version === 3) {
        set(component.$data, prop, newData);
      }

      if (version === 2) {
        set(component._data, prop, newData);
      }
    });
  });
};
const defaultGetterGenerator = prop => state => get(state, prop);

const bindOrFallback = (vueInstance, value, fallbackGetter) => {
  return typeof value === 'function' ? value.bind(vueInstance) : fallbackGetter(value);
};

const directCallOrFallback = (vueInstance, value, fallbackGetter, state) => {
  return typeof value === 'function' ? value.call(vueInstance, state) : fallbackGetter(value)(state);
};

const generateStoreBindingBySlices = (vueInstance, subState, slices, obj, fallbackGetter) => {
  return slices.reduce((result, prop) => Object.assign({}, result, {
    [subState]: Object.assign(Object.assign({}, result[subState]), {
      [prop]: bindOrFallback(vueInstance, obj[prop], fallbackGetter)
    })
  }), getStoreBinding(vueInstance) || {});
};
const objectMappers = (subState, obj, fallbackGetter) => function () {
  // @ts-ignore
  const vueInstance = this;
  const slices = Object.keys(obj);
  const state = get(getStore(vueInstance).getState(), subState);
  const calculatedBinding = generateStoreBindingBySlices(vueInstance, subState, slices, obj, fallbackGetter);
  setStoreBinding(vueInstance, calculatedBinding);
  return slices.reduce((result, prop) => Object.assign({}, result, {
    [prop]: directCallOrFallback(vueInstance, obj[prop], fallbackGetter, state)
  }), {});
};
/**
 * vue-router base name was changed in v4 from `base` to `history.base`
 * @param router VueRouter v3 or v4
 * @returns router base name
 */

const getRouterBaseName = router => {
  var _a, _b;

  return (_a = router === null || router === void 0 ? void 0 : router.options.base) !== null && _a !== void 0 ? _a : (_b = (router === null || router === void 0 ? void 0 : router.options).history) === null || _b === void 0 ? void 0 : _b.base;
};

class ActionsHolder {
  static getInstance() {
    if (!ActionsHolder.instance) {
      ActionsHolder.instance = new ActionsHolder();
    }

    return ActionsHolder.instance;
  }

  static setActions(actions) {
    ActionsHolder.getInstance().actions = actions;
  }

  static getAction(action) {
    return ActionsHolder.getInstance().actions[action];
  }

}

class FronteggAuthService {
  constructor(_a) {
    var {
      router
    } = _a,
        options = __rest(_a, ["router"]);

    this.state = authInitialState;
    this.userRef = undefined;

    this.init = (options, store) => {
      this.store = store;
      Object.entries({
        loginActions,
        socialLoginsActions,
        activateAccountActions,
        acceptInvitationActions,
        forgotPasswordActions,
        signUpActions,
        profileActions,
        ssoActions,
        mfaActions,
        teamActions,
        apiTokensActions,
        securityPolicyActions,
        tenantsActions
      }).forEach(([key, actions]) => {
        Object.assign(this, {
          [key]: bindActionCreators(actions, this.store.dispatch)
        });
      });
      const detachableActions = bindActionCreators(authActions, this.store.dispatch);
      ActionsHolder.setActions(detachableActions);

      if (!options.hostedLoginBox) {
        this.store.subscribe(this.storeSubscriber);
      }
    };

    this.storeSubscriber = () => {
      this.state = this.store.getState().auth;
      let notifyChange = false;

      if (this.state.user !== this.userRef) {
        this.userRef = this.state.user;
        notifyChange = true;
      }

      if (this.state.isAuthenticated !== this.isAuthenticatedRef) {
        this.isAuthenticatedRef = this.state.isAuthenticated;
        notifyChange = true;
      }

      if (!this.userRef || !this.state.isAuthenticated) {
        clearInterval(this.accessTokenUpdaterRef);
      } else if (notifyChange) {
        this.accessTokenUpdater();
      }
    };

    this.accessTokenUpdater = (firstTime = false) => {
      var _a;

      this.accessTokenUpdaterRef && clearInterval(this.accessTokenUpdaterRef);

      if (firstTime) {
        this.loginActions.requestAuthorize(true);
      } else {
        if (this.isAuthenticated) {
          const ttl = (((_a = this.user) === null || _a === void 0 ? void 0 : _a.expiresIn) || 20) * 1000 * 0.8;
          this.accessTokenUpdaterRef = setInterval(this.loginActions.requestAuthorize, ttl);
        }
      }
    };

    this.getCurrentRoute = () => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;

      let route = '/';

      if ((_b = (_a = this.router) === null || _a === void 0 ? void 0 : _a.currentRoute) === null || _b === void 0 ? void 0 : _b.path) {
        // vue-router v3
        route = (_d = (_c = this.router) === null || _c === void 0 ? void 0 : _c.currentRoute) === null || _d === void 0 ? void 0 : _d.path;
      } // @ts-ignore vue-router v4


      if ((_g = (_f = (_e = this.router) === null || _e === void 0 ? void 0 : _e.currentRoute) === null || _f === void 0 ? void 0 : _f.value) === null || _g === void 0 ? void 0 : _g.fullPath) {
        // @ts-ignore vue-router v4
        route = (_k = (_j = (_h = this.router) === null || _h === void 0 ? void 0 : _h.currentRoute) === null || _j === void 0 ? void 0 : _j.value) === null || _k === void 0 ? void 0 : _k.fullPath;
      }

      return route;
    };

    this.isAuthRoutes = path => {
      const pathname = new URL(path, window.location.origin).pathname;
      return Object.values(this.routes).filter(route => route != this.routes.authenticatedUrl).includes(pathname);
    };

    this._routes = Object.assign(Object.assign({}, authInitialState.routes), options.routes);
    this.router = router;
  }

  get loading() {
    var _a, _b;

    return (_b = (_a = this.store) === null || _a === void 0 ? void 0 : _a.getState().auth.isLoading) !== null && _b !== void 0 ? _b : true;
  }

  get routes() {
    var _a, _b;

    return (_b = (_a = this.store) === null || _a === void 0 ? void 0 : _a.getState().auth.routes) !== null && _b !== void 0 ? _b : this._routes;
  }

  get isAuthenticated() {
    var _a, _b;

    return (_b = (_a = this.store) === null || _a === void 0 ? void 0 : _a.getState().auth.isAuthenticated) !== null && _b !== void 0 ? _b : false;
  }

  get user() {
    var _a;

    return (_a = this.store) === null || _a === void 0 ? void 0 : _a.getState().auth.user;
  }

}

// @ts-ignore

const mapSubState = (statePrefix, propertyName) => function () {
  const obj = {
    [propertyName !== null && propertyName !== void 0 ? propertyName : statePrefix.substring('auth.'.length)]: state => state
  }; // @ts-ignore

  return objectMappers(statePrefix, obj, defaultGetterGenerator).bind(this)();
};

const mapAuthState = _this => mapSubState('auth', 'authState').bind(_this);
const mapLoginState = _this => mapSubState('auth.loginState').bind(_this);
const mapAcceptInvitationState = _this => mapSubState('auth.acceptInvitationState').bind(_this);
const mapActivateAccountState = _this => mapSubState('auth.activateState').bind(_this);
const mapApiTokensState = _this => mapSubState('auth.apiTokensState').bind(_this);
const mapForgotPasswordState = _this => mapSubState('auth.forgotPasswordState').bind(_this);
const mapMfaState = _this => mapSubState('auth.mfaState').bind(_this);
const mapProfileState = _this => mapSubState('auth.profileState').bind(_this);
const mapSecurityPolicyState = _this => mapSubState('auth.securityPolicyState').bind(_this);
const mapSignUpState = _this => mapSubState('auth.signUpState').bind(_this);
const mapSocialLoginState = _this => mapSubState('auth.socialLoginState').bind(_this);
const mapSsoState = _this => mapSubState('auth.ssoState').bind(_this);
const mapTeamState = _this => mapSubState('auth.teamState').bind(_this);
const mapTenantsState = _this => mapSubState('auth.tenantsState').bind(_this);
const connectMapState = _this => {
  Object.assign(_this, {
    mapAuthState: mapAuthState(_this),
    mapLoginState: mapLoginState(_this),
    mapAcceptInvitationState: mapAcceptInvitationState(_this),
    mapActivateAccountState: mapActivateAccountState(_this),
    mapApiTokensState: mapApiTokensState(_this),
    mapForgotPasswordState: mapForgotPasswordState(_this),
    mapMfaState: mapMfaState(_this),
    mapProfileState: mapProfileState(_this),
    mapSecurityPolicyState: mapSecurityPolicyState(_this),
    mapSignUpState: mapSignUpState(_this),
    mapSocialLoginState: mapSocialLoginState(_this),
    mapSsoState: mapSsoState(_this),
    mapTeamState: mapTeamState(_this),
    mapTenantsState: mapTenantsState(_this)
  });
};

const actionGetter = action => (...args) => ActionsHolder.getAction(action)(...args);

const mapAuthActions = action => actionGetter(action);
const mapLoginActions = action => {
  if (action === 'loginWithRedirect') {
    return actionGetter('requestHostedLoginAuthorize');
  }

  return actionGetter(action);
};
const mapAcceptInvitationActions = action => actionGetter(action);
const mapActivateAccountActions = action => actionGetter(action);
const mapApiTokensActions = action => actionGetter(action);
const mapForgotPasswordActions = action => actionGetter(action);
const mapMfaActions = action => actionGetter(action);
const mapProfileActions = action => actionGetter(action);
const mapSecurityPolicyActions = action => actionGetter(action);
const mapSignupActions = action => actionGetter(action);
const mapSocialLoginActions = action => actionGetter(action);
const mapSsoActions = action => actionGetter(action);
const mapTeamActions = action => actionGetter(action);
const connectFronteggStoreV3 = store => {
  const initialState = store.getState(); // Using for managing the store resubscription / unsubscribing to make sure we have only one subscription. Subscribe only when store unsubscribed

  if (store.subscribed) {
    // David suggestion: return the previous state and unsubscribe function if the store is already subscribed to fix unsubscribe issue when working with router-view
    return {
      authState: store.previousAuthState,
      unsubscribe: store.previousUnsubscribe
    };
  }

  const authState = reactive(Object.assign({}, initialState.auth));
  const unsubscribe = store.subscribe(() => {
    store.subscribed = true;
    const state = store.getState().auth;
    Object.entries(state).forEach(([key, value]) => {
      if (authState[key] !== state[key]) {
        authState[key] = value;
      }
    });
  });

  const unsubscribeWrapper = () => {
    store.subscribed = false;
    unsubscribe();
  };

  store.previousAuthState = authState;
  store.previousUnsubscribe = unsubscribeWrapper;
  return {
    authState,
    unsubscribe: unsubscribeWrapper
  };
};
const useFronteggLoaded = () => {
  const fronteggLoaded = inject(fronteggLoadedKey);
  return fronteggLoaded;
};
const useUnsubscribeFronteggStore = () => {
  const unsubscribeFronteggStore = inject(unsubscribeFronteggStoreKey);
  return unsubscribeFronteggStore;
};
const useAuthState = () => {
  const authState = inject(authStateKey);
  return authState;
};
const useFronteggStore = () => {
  const fronteggStore = inject(fronteggStoreKey);
  return fronteggStore;
};
const useFronteggAuth = () => {
  const fronteggAuth = inject(fronteggAuthKey);
  return fronteggAuth;
};
const useLoadEntitlements = () => {
  return inject(loadEntitlementsKey);
};
/**
 * @returns user state
 */

const useGetUserState = () => {
  const authState = inject(authStateKey);
  return authState.user;
};
/**
  @param options.maxAge max time in seconds for a valid login authentication session. The user will be require to re-login if the maxAge is not valid
  @returns whether the user is stepped up
*/


const useIsSteppedUp = options => {
  return computed(() => {
    const user = useGetUserState();
    return isSteppedUp(user, options);
  });
};
/**
  @param options.maxAge max time in seconds for a valid login authentication session. The user will be require to re-login if the maxAge is not valid
  @returns step up function that triggers the flow to step up the user if needed
*/

const useStepUp = () => {
  return inject(stepUpKey);
};
const useFeatureFlag = keys => {
  const {
    appName
  } = useFronteggStore();
  return FeatureFlags.getFeatureFlags(keys, appName);
};
const useFrontegg = () => {
  const fronteggLoaded = useFronteggLoaded();
  const authState = useAuthState();
  const fronteggAuth = useFronteggAuth();
  const loadEntitlements = useLoadEntitlements();
  const stepUp = useStepUp();
  const fronteggStore = useFronteggStore();

  const loginWithRedirect = () => {
    var _a;

    const path = fronteggAuth.getCurrentRoute();

    if (!path.startsWith((_a = authState.routes.hostedLoginRedirectUrl) !== null && _a !== void 0 ? _a : defaultFronteggRoutes.hostedLoginRedirectUrl)) {
      fronteggStore.dispatch({
        type: 'auth/setState',
        payload: {
          isLoading: true
        }
      });
      fronteggAuth.loginActions.requestHostedLoginAuthorize();
    }
  };

  return {
    fronteggLoaded,
    authState,
    fronteggAuth,
    loginWithRedirect,
    loadEntitlements,
    stepUp
  };
};
/**
 * Redirect to login page if the user is not authenticated
 * @param redirectUrl the url to redirect after hosted login
 */

const useFronteggAuthGuard = options => {
  const {
    redirectUrl
  } = options !== null && options !== void 0 ? options : {};
  const fronteggAuth = useFronteggAuth();
  const fronteggOptions = inject(fronteggOptionsKey);
  const authState = useAuthState();
  const router = inject(routerKey);
  const fronteggStore = useFronteggStore();

  const checkGuard = () => {
    const route = fronteggAuth.getCurrentRoute();

    if (!fronteggAuth.isAuthRoutes(route) && !authState.isAuthenticated && !authState.isLoading) {
      if (fronteggOptions.hostedLoginBox) {
        fronteggStore.dispatch({
          type: 'auth/setState',
          payload: {
            isLoading: true
          }
        });

        if (redirectUrl) {
          localStorage.setItem(FRONTEGG_AFTER_AUTH_REDIRECT_URL, redirectUrl);
        }

        fronteggAuth.loginActions.requestHostedLoginAuthorize();
      } else {
        router.push(authState.routes.loginUrl);
      }
    }
  };

  onMounted(checkGuard);
  onUpdated(checkGuard);
};

var sdkVersion = {
  version: '3.0.22'
};

var name = "vue";
var version = "2.6.12";
var description = "Reactive, component-oriented view layer for modern web interfaces.";
var main = "dist/vue.runtime.common.js";
var module = "dist/vue.runtime.esm.js";
var unpkg = "dist/vue.js";
var jsdelivr = "dist/vue.js";
var typings = "types/index.d.ts";
var files = [
	"src",
	"dist/*.js",
	"types/*.d.ts"
];
var sideEffects = false;
var scripts = {
	dev: "rollup -w -c scripts/config.js --environment TARGET:web-full-dev",
	"dev:cjs": "rollup -w -c scripts/config.js --environment TARGET:web-runtime-cjs-dev",
	"dev:esm": "rollup -w -c scripts/config.js --environment TARGET:web-runtime-esm",
	"dev:test": "karma start test/unit/karma.dev.config.js",
	"dev:ssr": "rollup -w -c scripts/config.js --environment TARGET:web-server-renderer",
	"dev:compiler": "rollup -w -c scripts/config.js --environment TARGET:web-compiler ",
	"dev:weex": "rollup -w -c scripts/config.js --environment TARGET:weex-framework",
	"dev:weex:factory": "rollup -w -c scripts/config.js --environment TARGET:weex-factory",
	"dev:weex:compiler": "rollup -w -c scripts/config.js --environment TARGET:weex-compiler ",
	build: "node scripts/build.js",
	"build:ssr": "npm run build -- web-runtime-cjs,web-server-renderer",
	"build:weex": "npm run build -- weex",
	test: "npm run lint && flow check && npm run test:types && npm run test:cover && npm run test:e2e -- --env phantomjs && npm run test:ssr && npm run test:weex",
	"test:unit": "karma start test/unit/karma.unit.config.js",
	"test:cover": "karma start test/unit/karma.cover.config.js",
	"test:e2e": "npm run build -- web-full-prod,web-server-basic-renderer && node test/e2e/runner.js",
	"test:weex": "npm run build:weex && jasmine JASMINE_CONFIG_PATH=test/weex/jasmine.js",
	"test:ssr": "npm run build:ssr && jasmine JASMINE_CONFIG_PATH=test/ssr/jasmine.js",
	"test:sauce": "npm run sauce -- 0 && npm run sauce -- 1 && npm run sauce -- 2",
	"test:types": "tsc -p ./types/test/tsconfig.json",
	lint: "eslint src scripts test",
	flow: "flow check",
	sauce: "karma start test/unit/karma.sauce.config.js",
	"bench:ssr": "npm run build:ssr && node benchmarks/ssr/renderToString.js && node benchmarks/ssr/renderToStream.js",
	release: "bash scripts/release.sh",
	"release:weex": "bash scripts/release-weex.sh",
	"release:note": "node scripts/gen-release-note.js",
	commit: "git-cz"
};
var gitHooks = {
	"pre-commit": "lint-staged",
	"commit-msg": "node scripts/verify-commit-msg.js"
};
var repository = {
	type: "git",
	url: "git+https://github.com/vuejs/vue.git"
};
var keywords = [
	"vue"
];
var author = "Evan You";
var license = "MIT";
var bugs = {
	url: "https://github.com/vuejs/vue/issues"
};
var homepage = "https://github.com/vuejs/vue#readme";
var devDependencies = {
	"@babel/core": "^7.0.0",
	"@babel/plugin-proposal-class-properties": "^7.1.0",
	"@babel/plugin-syntax-dynamic-import": "^7.0.0",
	"@babel/plugin-syntax-jsx": "^7.0.0",
	"@babel/plugin-transform-flow-strip-types": "^7.0.0",
	"@babel/preset-env": "^7.0.0",
	"@babel/register": "^7.0.0",
	"@types/node": "^12.12.0",
	"@types/webpack": "^4.4.22",
	acorn: "^5.2.1",
	"babel-eslint": "^10.0.1",
	"babel-helper-vue-jsx-merge-props": "^2.0.3",
	"babel-loader": "^8.0.4",
	"babel-plugin-istanbul": "^5.1.0",
	"babel-plugin-transform-vue-jsx": "^4.0.1",
	"babel-preset-flow-vue": "^1.0.0",
	buble: "^0.19.3",
	chalk: "^2.3.0",
	chromedriver: "^2.45.0",
	codecov: "^3.0.0",
	commitizen: "^2.9.6",
	"conventional-changelog": "^1.1.3",
	"cross-spawn": "^6.0.5",
	"cz-conventional-changelog": "^2.0.0",
	"de-indent": "^1.0.2",
	"es6-promise": "^4.1.0",
	escodegen: "^1.8.1",
	eslint: "^5.7.0",
	"eslint-plugin-flowtype": "^2.34.0",
	"eslint-plugin-jasmine": "^2.8.4",
	"file-loader": "^3.0.1",
	"flow-bin": "^0.61.0",
	"hash-sum": "^1.0.2",
	he: "^1.1.1",
	"http-server": "^0.11.1",
	jasmine: "^2.99.0",
	"jasmine-core": "^2.99.0",
	karma: "^3.1.1",
	"karma-chrome-launcher": "^2.1.1",
	"karma-coverage": "^1.1.1",
	"karma-firefox-launcher": "^1.0.1",
	"karma-jasmine": "^1.1.0",
	"karma-mocha-reporter": "^2.2.3",
	"karma-phantomjs-launcher": "^1.0.4",
	"karma-safari-launcher": "^1.0.0",
	"karma-sauce-launcher": "^2.0.2",
	"karma-sourcemap-loader": "^0.3.7",
	"karma-webpack": "^4.0.0-rc.2",
	"lint-staged": "^8.0.0",
	lodash: "^4.17.4",
	"lodash.template": "^4.4.0",
	"lodash.uniq": "^4.5.0",
	"lru-cache": "^5.1.1",
	nightwatch: "^0.9.16",
	"nightwatch-helpers": "^1.2.0",
	"phantomjs-prebuilt": "^2.1.14",
	puppeteer: "^1.11.0",
	resolve: "^1.3.3",
	rollup: "^1.0.0",
	"rollup-plugin-alias": "^1.3.1",
	"rollup-plugin-buble": "^0.19.6",
	"rollup-plugin-commonjs": "^9.2.0",
	"rollup-plugin-flow-no-whitespace": "^1.0.0",
	"rollup-plugin-node-resolve": "^4.0.0",
	"rollup-plugin-replace": "^2.0.0",
	"selenium-server": "^2.53.1",
	"serialize-javascript": "^3.1.0",
	shelljs: "^0.8.1",
	terser: "^3.10.2",
	typescript: "^3.6.4",
	webpack: "~4.28.4",
	"weex-js-runtime": "^0.23.6",
	"weex-styler": "^0.3.0",
	yorkie: "^2.0.0"
};
var config = {
	commitizen: {
		path: "./node_modules/cz-conventional-changelog"
	}
};
var VuePkg = {
	name: name,
	version: version,
	description: description,
	main: main,
	module: module,
	unpkg: unpkg,
	jsdelivr: jsdelivr,
	typings: typings,
	files: files,
	sideEffects: sideEffects,
	scripts: scripts,
	gitHooks: gitHooks,
	"lint-staged": {
	"*.js": [
		"eslint --fix",
		"git add"
	]
},
	repository: repository,
	keywords: keywords,
	author: author,
	license: license,
	bugs: bugs,
	homepage: homepage,
	devDependencies: devDependencies,
	config: config
};

// @ts-ignore
/**
 * @returns user state
 */

const useGetUserState$1 = () => {
  const authState = inject(authStateKey);
  return authState.user;
};
/**
 * @returns true when need to use entitlements V2 API
 */


const useIsV2API = () => {
  const [useEntitlementsV2] = useFeatureFlag([USE_ENTITLEMENTS_V2_ENDPOINT_FF]);
  return useEntitlementsV2;
};
/**
 * @param customAttributes consumer attributes
 * @returns is entitled query data including: entitltments state, final attributes (consumer and frontegg) and API version to use
 */


const useEntitlementsQueryData = customAttributes => {
  const user = useGetUserState$1();
  const entitlements = user === null || user === void 0 ? void 0 : user.entitlements;
  const isV2 = useIsV2API();
  const attributes = {
    custom: customAttributes,
    jwt: user
  };
  return {
    entitlements,
    attributes,
    isV2
  };
};
/**
  @param key feature key
  @param customAttributes consumer attributes
  @returns if the user is entitled to the given feature. Attaching the justification if not
  @throws when entitlement is not enabled via frontegg options
*/


const useFeatureEntitlements = (key, customAttributes) => {
  return computed(() => {
    const {
      entitlements,
      attributes,
      isV2
    } = useEntitlementsQueryData(customAttributes);
    return getFeatureEntitlements(entitlements, key, attributes, isV2);
  });
};
/**
  @param key permission key
  @param customAttributes consumer attributes
  @returns if the user is entitled to the given permission. Attaching the justification if not
  @throws when entitlement is not enabled via frontegg options
*/

const usePermissionEntitlements = (key, customAttributes) => {
  return computed(() => {
    const {
      entitlements,
      attributes,
      isV2
    } = useEntitlementsQueryData(customAttributes);
    return getPermissionEntitlements(entitlements, key, attributes, isV2);
  });
};
/**
  @param entitledToOptions - including permission or feature key
  @param customAttributes consumer attributes
  @returns if the user is entitled to the given permission or feature. Attaching the justification if not
  @throws when entitlement is not enabled via frontegg options
*/

const useEntitlements = (entitledToOptions, customAttributes) => {
  return computed(() => {
    const {
      entitlements,
      attributes,
      isV2
    } = useEntitlementsQueryData(customAttributes);
    return getEntitlements(entitlements, entitledToOptions, attributes, isV2);
  });
};

const isAuthenticatedGuard = function (_to, _from, next) {
  var _a, _b;

  const checkIfAuthenticated = () => {
    var _a;

    const state = StoreHolder.getStore().getState().auth;

    if (state.isAuthenticated) {
      next();
    } else {
      const basename = (_a = StoreHolder.getBasename()) !== null && _a !== void 0 ? _a : '';
      const calculatedBasename = basename.endsWith('/') ? basename.substring(0, basename.length - 1) : '';
      localStorage.setItem('FRONTEGG_AFTER_AUTH_REDIRECT_URL', `${calculatedBasename}${_to.fullPath}`);
      next({
        path: state.routes.loginUrl,
        replace: true
      });
    }
  };

  let store = (_b = (_a = StoreHolder.getStore()) === null || _a === void 0 ? void 0 : _a.getState()) === null || _b === void 0 ? void 0 : _b.auth;

  if (store && !(store === null || store === void 0 ? void 0 : store.isLoading)) {
    checkIfAuthenticated();
    return;
  }

  const waitFor = setInterval(() => {
    var _a, _b;

    store = (_b = (_a = StoreHolder.getStore()) === null || _a === void 0 ? void 0 : _a.getState()) === null || _b === void 0 ? void 0 : _b.auth;

    if (store && !(store === null || store === void 0 ? void 0 : store.isLoading)) {
      clearInterval(waitFor);
      checkIfAuthenticated();
    }
  }, 1);
};

let fronteggApp = null;
const Frontegg = {
  install(Vue, options) {
    var _a, _b;

    const _c = options !== null && options !== void 0 ? options : {},
          {
      router
    } = _c,
          rest = __rest(_c, ["router"]);

    const {
      contextOptions
    } = options !== null && options !== void 0 ? options : {};

    if (contextOptions == null) {
      throw Error('contextOptions must be passed to Vue.use(Frontegg, { /* OPTIONS */ })');
    }

    contextOptions.requestCredentials = (_a = contextOptions.requestCredentials) !== null && _a !== void 0 ? _a : 'include';
    contextOptions.metadataHeaders = {
      //TODO: remove this ts-ignore after updating rest-api context options type to accept string.
      //@ts-ignore
      framework: `${FronteggFrameworks.Vuejs}@${VuePkg.version}`,
      fronteggSdkVersion: `@frontegg/vuejs@${sdkVersion.version}`
    };
    ContextHolder.setContext(contextOptions);
    let pluginRegistered = false; // frontegg loader subscription

    let fronteggLoaded = false;
    const isVue3 = Vue.version.charAt(0) === '3';
    let fronteggLoadedV3;

    if (isVue3) {
      fronteggLoadedV3 = ref(false);
    }

    const fronteggLoadedSubscribes = new Set();

    const fronteggLoadedSubscribe = instance => {
      const func = () => {
        instance.fronteggLoaded = fronteggLoaded;
      };

      fronteggLoadedSubscribes.add(func);
      return () => fronteggLoadedSubscribes.delete(func);
    };

    const onRedirectTo = router && setupOnRedirectTo(router, (_b = options === null || options === void 0 ? void 0 : options.authOptions) === null || _b === void 0 ? void 0 : _b.routes);
    fronteggApp = initialize(Object.assign(Object.assign({}, rest), {
      onRedirectTo,
      basename: getRouterBaseName(router)
    }));
    const store = fronteggApp.store;
    StoreHolder.setStore(store); // @ts-ignore

    Vue.$fronteggApp = fronteggApp;

    if (!Vue.fronteggAuth) {
      Vue.fronteggAuth = new FronteggAuthService({
        router
      });
      registerFronteggPlugin(Vue, Vue.fronteggAuth);
    }

    const registerPlugins = instance => {
      pluginRegistered = true;
      (Vue.fronteggPlugins || []).forEach(plugin => plugin.init(rest, store));
      setStoreKey(instance, store);
      instance.fronteggAuth = Vue.fronteggAuth;
      connectMapState(instance);
    };

    const checkIfPluginsLoaded = () => {
      const _fronteggLoaded = (Vue.fronteggPlugins || []).reduce((loaded, plugin) => loaded && !plugin.loading, true);

      if (_fronteggLoaded !== fronteggLoaded) {
        fronteggLoaded = _fronteggLoaded;

        if (isVue3) {
          fronteggLoadedV3.value = _fronteggLoaded;
        }

        for (const subscriber of fronteggLoadedSubscribes) {
          subscriber();
        }
      }
    };

    router === null || router === void 0 ? void 0 : router.getRoutes().map(route => {
      const beforeEnterFn = route.beforeEnter;

      route.beforeEnter = (route, redirect, next) => {
        const callNext = () => {
          if (beforeEnterFn) {
            beforeEnterFn(route, redirect, next);
          } else {
            next();
          }
        };

        if (fronteggLoaded) {
          callNext();
          return;
        }

        const waitFor = setInterval(() => {
          checkIfPluginsLoaded();

          if (fronteggLoaded) {
            clearInterval(waitFor);
            callNext();
          }
        }, 1);
      };
    });
    const waitFor = setInterval(() => {
      checkIfPluginsLoaded();

      if (fronteggLoaded) {
        clearInterval(waitFor);
      }
    }, 10);

    const authorizedContentGuard = _this => {
      if (!_this.authorizedContent || Vue.fronteggAuth.isAuthRoutes(_this.$route.path)) {
        return;
      }

      if (!Vue.fronteggAuth.loading && !Vue.fronteggAuth.isAuthenticated) {
        if (options === null || options === void 0 ? void 0 : options.hostedLoginBox) {
          Vue.fronteggAuth.loginActions.requestHostedLoginAuthorize();
        } else {
          const {
            loginUrl
          } = Vue.fronteggAuth.routes;

          _this.$router.push(loginUrl);
        }
      }
    };

    if (!pluginRegistered) {
      registerPlugins(Vue);
    } // should be function not arrow to maintain this


    function loginWithRedirect() {
      // @ts-ignore
      if (!this.$route.path.startsWith(Vue.fronteggAuth.routes.hostedLoginRedirectUrl)) {
        store.dispatch({
          type: 'auth/setState',
          payload: {
            isLoading: true
          }
        });
        Vue.fronteggAuth.loginActions.requestHostedLoginAuthorize();
      }
    }

    const loadEntitlements = fronteggApp.loadEntitlements.bind(fronteggApp);
    const stepUp = fronteggApp.stepUp.bind(fronteggApp);

    if (isVue3) {
      // @ts-ignore - provide will exist only in vue 3 app
      Vue.provide(fronteggLoadedKey, fronteggLoadedV3);
      const {
        authState,
        unsubscribe
      } = connectFronteggStoreV3(store);
      const fronteggAuthReactive = reactive(Object.assign({}, Vue.fronteggAuth)); // @ts-ignore

      Vue.provide(authStateKey, authState); // @ts-ignore

      Vue.provide(unsubscribeFronteggStoreKey, unsubscribe); // @ts-ignore

      Vue.provide(fronteggAuthKey, fronteggAuthReactive); // @ts-ignore

      Vue.provide(routerKey, router); // @ts-ignore

      Vue.provide(fronteggOptionsKey, options); // @ts-ignore

      Vue.provide(fronteggStoreKey, store); // @ts-ignore

      Vue.provide(loadEntitlementsKey, loadEntitlements); // @ts-ignore

      Vue.provide(stepUpKey, stepUp);
    }

    Vue.mixin({
      data: () => ({
        fronteggLoaded
      }),

      beforeCreate() {
        setStoreKey(this, store);
        this.fronteggAuth = Vue.fronteggAuth;
        this.loginWithRedirect = loginWithRedirect.bind(this); // _user was added for to make the computed property reactive, then it will get updated

        this.getFeatureEntitlements = (_user, key, customAttributes) => fronteggApp.getFeatureEntitlements(key, customAttributes);

        this.getPermissionEntitlements = (_user, key, customAttributes) => fronteggApp.getPermissionEntitlements(key, customAttributes);

        this.getEntitlements = (_user, entitledToOptions, customAttributes) => fronteggApp.getEntitlements(entitledToOptions, customAttributes);

        this.loadEntitlements = loadEntitlements;

        this.isSteppedUp = _user => fronteggApp.isSteppedUp();

        connectMapState(this);
      },

      updated() {
        authorizedContentGuard(this);
      },

      mounted() {
        authorizedContentGuard(this);
      },

      created() {
        if (getStoreBinding(this)) {
          setStoreUnsubscribe(this, store.subscribe(syncStateWithComponent(this, getStoreBinding(this))));
        }

        setLoadingUnsubscribe(this, fronteggLoadedSubscribe(this));
      },

      beforeDestroy() {
        storeUnsubscribe(this);
        loadingUnsubscribe(this);
      }

    });
  }

};
/**
 * @deprecated since version 2.0
 * use instead:
 * import {AdminPortal} from '@frontegg/vue'
 * AdminPortal.show()
 */

const openAdminPortal = () => AdminPortal.show();
/**
 * @deprecated since version 2.0
 * use instead:
 * import {AdminPortal} from '@frontegg/vue'
 * AdminPortal.hide()
 */


const closeAdminPortal = () => AdminPortal.hide();

export { Frontegg, closeAdminPortal, isAuthenticatedGuard, mapAcceptInvitationActions, mapActivateAccountActions, mapApiTokensActions, mapAuthActions, mapForgotPasswordActions, mapLoginActions, mapMfaActions, mapProfileActions, mapSecurityPolicyActions, mapSignupActions, mapSocialLoginActions, mapSsoActions, mapTeamActions, openAdminPortal, useAuthState, useEntitlements, useFeatureEntitlements, useFrontegg, useFronteggAuthGuard, useFronteggLoaded, useIsSteppedUp, usePermissionEntitlements, useUnsubscribeFronteggStore };
